import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Console } from './logger.service';

/**
 * Servizio per la gestione automatica del loading.
 * Mostra un loading solo se la richiesta supera un determinato tempo (default: 500ms).
 * Utilizza Bootstrap per la visualizzazione del loading.
 * Gestisce correttamente le richieste multiple simultanee.
 */
@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  private readonly document = inject(DOCUMENT);
  private loadingElement: HTMLElement | null = null;
  private loadingTimer: ReturnType<typeof setTimeout> | null = null;
  private activeRequestsCount = 0; // Contatore delle richieste attive
  private readonly LOADING_DELAY = 500; // 500ms

  /**
   * Avvia il timer per il loading.
   * Se la richiesta non viene completata entro LOADING_DELAY, mostra il loading.
   * Incrementa il contatore delle richieste attive.
   */
  startLoading(): void {
    this.activeRequestsCount++;
    
    if (!this.loadingTimer) {
      this.loadingTimer = setTimeout(() => {
        if (this.activeRequestsCount > 0) {
          this.showLoading();
        }
      }, this.LOADING_DELAY);
    }
  }

  /**
   * Nasconde il loading e cancella il timer solo se non ci sono altre richieste attive.
   * Decrementa il contatore delle richieste attive.
   */
  stopLoading(): void {
    this.activeRequestsCount--;
    
    // Assicurati che il contatore non vada sotto zero
    if (this.activeRequestsCount < 0) {
      this.activeRequestsCount = 0;
    }
    
    // Se ci sono ancora richieste attive, non nascondere il loading
    if (this.activeRequestsCount > 0) {
      return;
    }
    
    // Se non ci sono più richieste attive, cancella il timer e nascondi il loading
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
    }
    this.hideLoading();
  }

  /**
   * Mostra il loading se non è già visibile.
   */
  private showLoading(): void {
    if (!this.loadingElement) {
      try {
        // Crea il container del loading
        this.loadingElement = this.document.createElement('div');
        this.loadingElement.id = 'loading-overlay';
        this.loadingElement.className = 'loading-overlay';
        this.loadingElement.setAttribute('role', 'status');
        this.loadingElement.setAttribute('aria-live', 'polite');
        this.loadingElement.setAttribute('aria-label', 'Caricamento in corso');

        // Crea il backdrop
        const backdrop = this.document.createElement('div');
        backdrop.className = 'loading-backdrop';

        // Crea il contenuto del loading
        const content = this.document.createElement('div');
        content.className = 'loading-content';

        // Crea lo spinner Bootstrap
        const spinner = this.document.createElement('div');
        spinner.className = 'spinner-border text-primary';
        spinner.setAttribute('role', 'status');
        spinner.innerHTML = '<span class="visually-hidden">Caricamento...</span>';

        // Crea il messaggio
        const message = this.document.createElement('div');
        message.className = 'loading-message mt-3';
        message.textContent = 'Caricamento dati in corso...';

        // Assembla il DOM
        content.appendChild(spinner);
        content.appendChild(message);
        this.loadingElement.appendChild(backdrop);
        this.loadingElement.appendChild(content);

        // Aggiungi al body
        this.document.body.appendChild(this.loadingElement);

        // Aggiungi classe per animazione
        setTimeout(() => {
          if (this.loadingElement) {
            this.loadingElement.classList.add('show');
          }
        }, 10);
      } catch (error) {
        Console.log('Errore nella creazione del loading:', error, 'warn');
      }
    }
  }

  /**
   * Nasconde il loading se è visibile.
   */
  private hideLoading(): void {
    if (this.loadingElement) {
      try {
        // Rimuovi classe show per animazione di uscita
        this.loadingElement.classList.remove('show');
        
        // Rimuovi l'elemento dopo l'animazione
        setTimeout(() => {
          if (this.loadingElement && this.loadingElement.parentNode) {
            this.loadingElement.parentNode.removeChild(this.loadingElement);
          }
          this.loadingElement = null;
        }, 200); // Tempo di animazione
      } catch (error) {
        // Ignora errori di rimozione (es. se già rimosso)
        Console.log('Errore nella rimozione del loading:', error, 'debug');
        this.loadingElement = null;
      }
    }
  }
}
